import { Heading, Text } from '@radix-ui/themes';
import clsx from 'clsx';
import { HubertButton } from 'components/HubertUI/HubertButton';
import HubertCombobox from 'components/HubertUI/HubertCombobox';
import HubertDialog from 'components/HubertUI/HubertDialog';
import IsAxiosError from 'components/common/IsAxiosError';
import MutedText from 'components/common/MutedText';
import { revisionLacksSharingPermission } from 'components/Templates/builder/TemplateRevision/revisionAccessGroups';
import type { ComponentProps } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  useArchiveTemplatesByProcessIdMutation,
  useCreateTemplateFromRevisionMutation,
  useDeleteTemplatesByProcessIdMutation,
} from 'rtk/query/apis/templates';
import { useAppSelector } from 'rtk/query/hooks';
import { toast } from 'sonner';
import type { TSelectBase } from 'types/general.types';
import type { TTemplate } from 'types/template/template.types';
import { isHubertAdminUser, isRecruiterUser, userHasPermission, userHasResourcePermission } from 'types/user.types';
import { TEMPLATE_PERMISSIONS } from '@hubertai/screening-question-types';

interface Props extends ComponentProps<'div'> {
  selectedTemplates: TTemplate[];
  onComplete?: () => void;
}

const SelectedTemplateActionsDialog = ({ selectedTemplates = [], onComplete }: Props) => {
  const { t } = useTranslation('PaginatedManagerTable');
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState<TSelectBase>();
  const user = useAppSelector((state) => state.config.user);

  const handleSuccess = () => {
    setOpen(false);
    onComplete?.();
  };

  const [
    archiveTemplatesByProcessIdRequest,
    { isLoading: isArchiveLoading, isError: isArchiveError, error: archiveError },
  ] = useArchiveTemplatesByProcessIdMutation();

  const [
    createTemplateFromRevisionRequest,
    { isLoading: isCreateFromRevisionLoading, isError: isCreateFromRevisionError, error: createFromRevisionError },
  ] = useCreateTemplateFromRevisionMutation();

  const [
    deleteTemplatesByProcessIdRequest,
    { isLoading: isDeleteLoading, isError: isDeleteError, error: deleteError },
  ] = useDeleteTemplatesByProcessIdMutation();

  // manageable: templates the user can archive or delete (requires Delete / ManageClientLevel).
  // creatable: templates the user can use as a revision source (requires Create / ManageClientLevel).
  // Admins can act on everything; for recruiters the two sets are derived from their access-group permissions.
  const manageableReferenceIds = new Set<string>();
  const creatableReferenceIds = new Set<string>();

  const userClientId = isRecruiterUser(user) ? user.client_id?.toString() : undefined;

  selectedTemplates.forEach((t) => {
    const isOwnClient = t.client_id?.toString() === userClientId;

    if (isHubertAdminUser(user)) {
      manageableReferenceIds.add(t.template_reference_id);
      creatableReferenceIds.add(t.template_reference_id);
      return;
    }

    // ManageClientLevel grants full manage + create rights on own-client templates.
    if (isOwnClient && userHasPermission(user, TEMPLATE_PERMISSIONS.ManageClientLevel)) {
      manageableReferenceIds.add(t.template_reference_id);
      creatableReferenceIds.add(t.template_reference_id);
      return;
    }

    // Create is role-level (not resource-scoped), any access group with it grants the right
    // to create from an archived template in the same client — except local ones, which need ManageClientLevel.
    if (isOwnClient && t.visibility !== 'local' && userHasPermission(user, TEMPLATE_PERMISSIONS.Create)) {
      creatableReferenceIds.add(t.template_reference_id);
    }

    // Delete is resource-level — the access group must explicitly grant this template.
    if (userHasResourcePermission(user, TEMPLATE_PERMISSIONS.Delete, t.template_reference_id, 'template')) {
      manageableReferenceIds.add(t.template_reference_id);
    }
  });

  const canManage = (t: TTemplate) => manageableReferenceIds.has(t.template_reference_id);
  const canCreate = (t: TTemplate) => creatableReferenceIds.has(t.template_reference_id);

  // Current and draft templates: can be archived or deleted (requires TEMPLATE_PERMISSIONS.Delete / ManageClientLevel).
  // Archived templates: can have a new template created from them (requires TEMPLATE_PERMISSIONS.Create / ManageClientLevel).
  const archivableTemplates = selectedTemplates.filter(
    (t) => canManage(t) && (t.is_current || t.is_draft) && !t.is_archived,
  );
  const archivableProcessIds = archivableTemplates.map((t) => t.process_id);
  // Archiving a current template also retires its whole family (any sibling drafts).
  // We surface this to the user so the scope of the action is clear.
  const archivableCurrentCount = archivableTemplates.filter((t) => t.is_current).length;

  const revisableTemplates = selectedTemplates.filter((t) => canCreate(t) && t.is_archived);
  const revisableProcessIds = revisableTemplates.map((t) => t.process_id);

  const deletableProcessIds = [...archivableProcessIds, ...revisableProcessIds];

  const actionList: TSelectBase[] = [
    ...(archivableProcessIds.length > 0
      ? [
          {
            value: 'archive',
            label: t('templateActions.actions.archive.label'),
            variant: 'destructive' as const,
            color: 'warning',
            onClick: () => {},
          },
        ]
      : []),
    ...(revisableProcessIds.length > 0
      ? [
          {
            value: 'create-from-revision',
            label: t('templateActions.actions.createFromRevision.label'),
            variant: 'destructive' as const,
            color: 'info',
            onClick: () => {},
          },
        ]
      : []),
    ...(deletableProcessIds.length > 0
      ? [
          {
            value: 'delete',
            label: t('templateActions.actions.delete.label'),
            variant: 'destructive' as const,
            color: 'danger',
            onClick: () => {},
          },
        ]
      : []),
  ];

  const relevantProcessIds =
    value?.value === 'archive'
      ? archivableProcessIds
      : value?.value === 'create-from-revision'
        ? revisableProcessIds
        : deletableProcessIds;

  const isAnyLoading = isArchiveLoading || isCreateFromRevisionLoading || isDeleteLoading;

  return (
    <HubertDialog
      open={open}
      onOpenChange={(o) => {
        setOpen(o);
        if (!o) {
          setValue(undefined);
        }
      }}
    >
      <HubertDialog.Trigger>
        <HubertButton
          disabled={selectedTemplates.length === 0}
          variant={'outline'}
          size={'sm'}
          onClick={() => setOpen(true)}
        >
          {t('templateActions.triggerButton')}
        </HubertButton>
      </HubertDialog.Trigger>
      <HubertDialog.Content
        position="center"
        className={clsx('min-h-[50%]', 'flex flex-col justify-between gap-4', 'p-4')}
        portalContent={false}
      >
        <div className="flex flex-col justify-start gap-4">
          <div className="flex flex-row items-baseline justify-start gap-2">
            <Heading size={'6'}>{t('templateActions.heading')}</Heading>
            <MutedText>
              {t('templateActions.subtitle', { count: deletableProcessIds.length, total: selectedTemplates.length })}
            </MutedText>
          </div>

          <div className="hubert-vertical-center flex flex-row justify-start gap-4">
            <Text>{t('templateActions.chooseAction')}</Text>
            <HubertCombobox
              itemName="item"
              items={actionList}
              disabled={actionList.length === 0}
              onValueChange={(v) => {
                const match = actionList.find((x) => x.value === v);
                setValue(match ?? undefined);
              }}
            />
          </div>
        </div>

        <div className={clsx('overflow-y-auto', 'max-h-96')}>
          {selectedTemplates.length ? (
            selectedTemplates.map((x, index) => (
              <div key={`selected-template-${index}`} className="m-2 flex gap-4 rounded-md border p-2">
                <span>{x.name}</span>
                {value?.value === 'archive' && x.is_current && canManage(x) && (
                  <span className="flex items-center text-sm text-amber-600">
                    {t('templateActions.actions.archive.familyTag')}
                  </span>
                )}
                {!canManage(x) && !canCreate(x) && (
                  <span className="flex items-center text-sm text-red-500">
                    {t('templateActions.missingPermissions')}
                  </span>
                )}
              </div>
            ))
          ) : (
            <Text className="flex justify-center" as="div">
              {t('templateActions.noSelection')}
            </Text>
          )}
        </div>

        {isArchiveError ? <IsAxiosError error={archiveError} /> : null}
        {isCreateFromRevisionError ? <IsAxiosError error={createFromRevisionError} /> : null}
        {isDeleteError ? <IsAxiosError error={deleteError} /> : null}

        {value ? (
          <div className={clsx(`bg-${value.color}-50 rounded-md p-4`)}>
            <div
              className={clsx(
                'flex flex-col items-center justify-center gap-2 align-middle',
                `text-${value.color}-900`,
                'min-h-20',
              )}
            >
              <Text as="span">
                {t('templateActions.confirmQuestion', { action: value.label, count: relevantProcessIds.length })}
              </Text>
              {value.value === 'archive' && archivableCurrentCount > 0 && (
                <Text as="p" className="text-sm text-amber-700">
                  {t('templateActions.actions.archive.familyWarning', { count: archivableCurrentCount })}
                </Text>
              )}
            </div>

            <div className="hubert-vertical-center flex flex-row justify-center gap-4">
              <HubertButton
                variant={value.variant ?? 'info'}
                onClick={async () => {
                  if (!value?.value.length) return;

                  if (value.value === 'archive') {
                    archiveTemplatesByProcessIdRequest({ processIds: archivableProcessIds })
                      .unwrap()
                      .then((result) => {
                        if (result.modifiedCount > 0) {
                          toast.success(t('templateActions.toast.archiveSuccess', { count: result.modifiedCount }));
                          handleSuccess();
                        } else {
                          toast.warning(t('templateActions.toast.archiveNoneModified'));
                        }
                      })
                      .catch((rejected) => {
                        console.log('archiveTemplatesByProcessIdRequest rejected', rejected);
                        toast.error(t('templateActions.toast.genericError', { error: JSON.stringify(rejected) }));
                      });
                  } else if (value.value === 'create-from-revision') {
                    const blockedRevisions = revisableTemplates.filter((t) =>
                      revisionLacksSharingPermission(user, t.visibility),
                    );
                    if (blockedRevisions.length > 0) {
                      toast.error(
                        t('templateActions.toast.createFromRevisionMissingSharingPermission', {
                          count: blockedRevisions.length,
                        }),
                      );
                    }
                    const restorableRevisions = revisableTemplates.filter((t) => !blockedRevisions.includes(t));
                    const results = await Promise.allSettled(
                      restorableRevisions.map((revision) =>
                        createTemplateFromRevisionRequest({ processId: revision.process_id }).unwrap(),
                      ),
                    );
                    const succeeded = results.filter((r) => r.status === 'fulfilled').length;
                    const failed = results.length - succeeded;
                    if (succeeded > 0) {
                      toast.success(t('templateActions.toast.createFromRevisionSuccess', { count: succeeded }));
                      handleSuccess();
                    }
                    if (failed > 0) {
                      toast.error(t('templateActions.toast.createFromRevisionError', { count: failed }));
                    }
                  } else if (value.value === 'delete') {
                    deleteTemplatesByProcessIdRequest({ processIds: deletableProcessIds })
                      .unwrap()
                      .then((result) => {
                        if (result.modifiedCount > 0) {
                          toast.success(t('templateActions.toast.deleteSuccess', { count: result.modifiedCount }));
                          handleSuccess();
                        } else {
                          toast.warning(t('templateActions.toast.deleteNoneModified'));
                        }
                      })
                      .catch((rejected) => {
                        console.log('deleteTemplatesByProcessIdRequest rejected', rejected);
                        toast.error(t('templateActions.toast.genericError', { error: JSON.stringify(rejected) }));
                      });
                  } else {
                    toast.error(t('templateActions.toast.notImplemented', { action: value.value }));
                  }
                }}
                disabled={relevantProcessIds.length === 0 || !value}
                loading={isAnyLoading}
              >
                {t('templateActions.confirmButton', { action: value.label })}
              </HubertButton>
            </div>
          </div>
        ) : (
          <div className="bg-danger-50 rounded-md p-4">
            <Text className="text-danger-500 flex justify-center" as="div">
              {t('templateActions.choosePrompt')}
            </Text>
          </div>
        )}
      </HubertDialog.Content>
    </HubertDialog>
  );
};

export default SelectedTemplateActionsDialog;
